import { useState, useEffect, useCallback, useMemo } from 'react'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { EXPORT_TYPE_EXCEL, EXPORT_TYPE_PDF, MAIN_TAB } from '@constants'
import { baseUrl } from '@api/ParseAPI'
import { createSyntheticDownload, downloadAllFilesBlobs, reconstituteBlobFromJson } from '@utils'
import JSZip from 'jszip'
import { PENDO_EVENTS, pendoTrack } from '@/utils/pendo'
import { toast } from 'react-toastify'
import { useSearchParams } from 'react-router-dom'

const ExportRunsheetDialogViewModel = documents => {
    
    const [searchParams] = useSearchParams()
    
    const [error, setError] = useState(null)
    const [selectedTypes, setSelectedTypes] = useState([EXPORT_TYPE_EXCEL, EXPORT_TYPE_PDF])
    const [tabToExport, setTabToExport] = useState('')
    const [templates, setTemplates] = useState({})
    const [sheet, setSheet] = useState(null)
    const [isDownloading, setIsDownloading] = useState(false)
    const [downloadProgressMax, setDownloadProgressMax] = useState(0)
    const [downloadProgressValue, setDownloadProgressValue] = useState(0)
    
    const [open, setOpen] = useWireState(store.exportRunsheetDialogOpen)
    const currentOrganization = useWireValue(store.currentOrganization)
    const runsheet = useWireValue(store.currentRunsheet)
    const exportTemplates = useWireValue(store.exportTemplates)
    const tabs = useWireValue(store.runsheetTabs)
    const [selectedTemplate, setSelectedTemplate] = useWireState(store.exportRunsheetSelectedTemplate)
    
    const activeTab = useMemo(() => searchParams.get('activeTab') || MAIN_TAB, [searchParams])
    const currentTab = useMemo(() => tabs.find(t => t.id === activeTab) || tabs[0], [tabs, activeTab])
    
    const closeDialog = () => {
        
        setError(null)
        setOpen(false)
        
    }
    
    const fetchExportTemplates = useCallback(async () => {
        
        try {
            
            if (!currentOrganization) return
            
            await actions.getExportTemplatesByOrgId(currentOrganization.id)
            
        } catch (e) {
            
            console.error('tpls all orgs failed', e)
            
        }
        
    }, [currentOrganization])
    
    const onExportRunsheetClick = useCallback(async () => {
        
        setError(null)
        setIsDownloading(true)
        setDownloadProgressMax(0)
        setDownloadProgressValue(0)
        
        try {
            
            const { archiveName, excel, pdfFileUrls } = await actions.exportRunsheet(
                runsheet.id, documents, activeTab, {
                    type: selectedTypes,
                    templateId: selectedTemplate.id,
                    tabToExport,
                })
            
            pendoTrack(PENDO_EVENTS.RUNSHEET_EXPORT, {
                runsheetId: runsheet.id,
                documentIds: documents,
                type: selectedTypes,
                templateId: selectedTemplate.id,
            })
            
            let blob
            
            if (excel.data) {
                
                blob = reconstituteBlobFromJson(excel.data.data, excel.mimeType)
                
                if (!pdfFileUrls?.length) {
                    // Just download the Excel file
                    createSyntheticDownload(blob, excel.mimeType, excel.name)
                    return closeDialog()
                }
                
            }
            
            setDownloadProgressMax(pdfFileUrls.length)
            
            // Download all PDF files, then create a zip file
            const zip = new JSZip()
            const urls = pdfFileUrls.map(it => `${baseUrl}/${it}`)
            const files = await downloadAllFilesBlobs(urls, false, () => {
                setDownloadProgressValue(prev => prev + 1)
            })
            
            if (blob) {
                // Add the spreadsheet to the zip file
                zip.file(excel.name, blob)
            }
            
            // Add all the PDF blobs to the zip file
            files.forEach(file => {
                if (file?.data)
                    zip.file(file.name, file.data)
            })
            
            const content = await zip.generateAsync({ type: 'blob' })
            
            createSyntheticDownload(content, 'application/zip', archiveName)
            
            const failedFiles = files.filter(it => !it?.data)
            
            if (failedFiles?.length)
                toast.warn(`Failed to export ${failedFiles.length} of ${files.length} files`)
            
            closeDialog()
            
        } catch (e) {
            
            console.error('export err', e)
            setError(`Please ensure you have selected a runsheet template and try again. 
                If this issue persists, please contact customer support.`)
            
        } finally {
            setIsDownloading(false)
            setDownloadProgressMax(0)
            setDownloadProgressValue(0)
        }
        
    }, [runsheet, documents, selectedTypes, selectedTemplate, tabToExport])
    
    const onExportTypeChange = type => {
        
        const updatedType = selectedTypes.includes(type)
            ? selectedTypes.filter(item => item !== type)
            : [...selectedTypes, type]
        
        setSelectedTypes(updatedType)
        
    }
    
    const onTabToExportChange = type => setTabToExport(type)
    
    const onTemplateChange = e => setSelectedTemplate(
        templates[EXPORT_TYPE_EXCEL].find(it => it.id === e.target.value))
    
    const onSheetChange = e => setSheet(e.target.value)
    
    useEffect(() => {
        
        fetchExportTemplates().catch(e => {
            console.error('useExportRunsheetDialogViewModel#fetchExportTemplates', e)
            toast.error('Failed to fetch templates. Please close this dialog & try again.')
        })
        
    }, [currentOrganization])
    
    useEffect(() => {
        
        const initialTemplate = exportTemplates?.length
            ? exportTemplates[0]
            : null
        
        setTemplates({
            [EXPORT_TYPE_PDF]: exportTemplates.filter(t => t.type === EXPORT_TYPE_PDF),
            [EXPORT_TYPE_EXCEL]: exportTemplates.filter(t => t.type === EXPORT_TYPE_EXCEL),
        })
        
        if (initialTemplate && (!selectedTemplate?.id || !exportTemplates.some(et => et.id === selectedTemplate.id)))
            console.log('set init tpl', selectedTemplate) || setSelectedTemplate(initialTemplate)
        
        setSheet(initialTemplate?.defaultSheet ?? null)
        
    }, [selectedTemplate, exportTemplates])
    
    useEffect(() => {
        setTabToExport('')
        if (activeTab === MAIN_TAB)
            setTabToExport('main')
    }, [activeTab])
    
    return {
        
        // State
        error,
        setError,
        selectedTypes,
        setSelectedTypes,
        tabToExport,
        setTabToExport,
        templates,
        setTemplates,
        sheet,
        setSheet,
        isDownloading,
        setIsDownloading,
        downloadProgressMax,
        setDownloadProgressMax,
        downloadProgressValue,
        setDownloadProgressValue,
        
        // Global State
        open,
        setOpen,
        runsheet,
        exportTemplates,
        selectedTemplate,
        activeTab,
        currentTab,
        setSelectedTemplate,
        
        // Methods
        closeDialog,
        onExportRunsheetClick,
        onExportTypeChange,
        onTabToExportChange,
        onTemplateChange,
        onSheetChange,
        
    }
    
}

export default ExportRunsheetDialogViewModel
